/**
 * Fetches a passkey ceremony's options BEFORE the person clicks.
 *
 * Browsers only show the passkey prompt in response to a click, and some
 * (Safari) stop counting the click once the page has waited on the network in
 * between. So the options are fetched ahead of time, and the click calls the
 * browser straight away with them. A challenge is single use and lives five
 * minutes on the server: `take` hands out the prefetched options once, if
 * still fresh, and otherwise fetches new ones (the prompt may then need a
 * second click on such a browser — the screens say "try again").
 */
import { useCallback, useEffect, useRef } from 'react';

/** Comfortably inside the server's five-minute challenge. */
const FRESH_MS = 4 * 60_000;

export function usePrefetchedPasskeyOptions<T>(fetcher: () => Promise<T>, enabled: boolean) {
  const cached = useRef<{ value: T; at: number } | null>(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const prefetch = useCallback(() => {
    cached.current = null;
    void fetcherRef
      .current()
      .then((value) => {
        cached.current = { value, at: Date.now() };
      })
      .catch(() => {
        cached.current = null;
      });
  }, []);

  useEffect(() => {
    if (enabled) prefetch();
  }, [enabled, prefetch]);

  /** The options to use now — each set only once. */
  const take = useCallback(async (): Promise<T> => {
    const fresh = cached.current;
    cached.current = null;
    if (fresh && Date.now() - fresh.at < FRESH_MS) return fresh.value;
    return fetcherRef.current();
  }, []);

  return { take, prefetch };
}
